import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import { DarkModeProvider, useDarkMode } from "./contexts/DarkModeContext";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import HeroSafari from "./components/HeroSafari";
import { detectSafari } from "./utils/browserDetection";
import EventFormat from "./components/EventFormat";
import Schedule from "./components/Schedule";
import Speakers from "./components/Speakers";
import Rubric from "./components/Rubric";
import Sponsors from "./components/Sponsors";
import FAQ from "./components/FAQ";
import Footer from "./components/Footer";
import RegisterPage from "./pages/RegisterPage";
import LivestreamPage from "./pages/LivestreamPage";
import AdminLogin from "./components/admin/AdminLogin";
import AdminDashboard from "./components/admin/AdminDashboard";
import AdminApplicant from "./components/admin/AdminApplicant";
import AdminInvite from "./components/admin/AdminInvite";
import SponsorContact from "./components/SponsorContact";
import SupportContact from "./components/SupportContact";
import PrivacyPolicy from "./pages/PrivacyPolicy";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

function App() {
  return (
    <DarkModeProvider>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/apply" element={<RegisterPage />} />
          <Route path="/livestream" element={<LivestreamPage />} />
          <Route path="/sponsor-contact" element={<SponsorContact />} />
          <Route path="/support-contact" element={<SupportContact />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/invite" element={<AdminInvite />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/applicant/:id" element={<AdminApplicant />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </Router>
    </DarkModeProvider>
  );
}

function HomePage() {
  const [activeSection, setActiveSection] = useState("home");
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    setIsSafari(detectSafari());
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
  };

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "-50% 0px -50% 0px",
      threshold: 0,
    };

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    // Observe all sections
    const sections = [
      "home",
      "schedule",
      "speakers",
      "rubric",
      "sponsors",
      "faq",
    ];
    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      sections.forEach((sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      <Navbar scrollToSection={scrollToSection} activeSection={activeSection} />

      <main>
        <section id="home">{isSafari ? <HeroSafari /> : <Hero />}</section>

        <section id="eventformat">
          <EventFormat />
        </section>

        <section id="schedule">
          <Schedule />
        </section>

        <section id="speakers">
          <Speakers />
        </section>

        <section id="rubric">
          <Rubric />
        </section>

        <section id="sponsors">
          <Sponsors />
        </section>

        <section id="faq">
          <FAQ />
        </section>
      </main>

      {/* Footer */}
      <Footer />

      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
}

export default App;
